import React, { useState, useEffect, useCallback } from "react";
import { Users, Filter, Clock } from "lucide-react";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import hrService from "../../../services/hrService";
import Select from '../../../components/ui/Select';
import { formatEasternDateOnly } from '../../../utils/timezone';

const AttendanceFilters = ({ onFilterChange }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [dateFilterMode, setDateFilterMode] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear()?.toString());
  const [loading, setLoading] = useState(false);

  // Generate year options (current year and 2 years back)
  const currentYear = new Date()?.getFullYear();
  const yearOptions = Array.from({ length: 3 }, (_, i) => ({
    value: (currentYear - i)?.toString(),
    label: (currentYear - i)?.toString()
  }));

  // Month options
  const monthOptions = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  // Load employees on mount
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        console.log('🔄 AttendanceFilters: Loading employees...');
        const data = await hrService?.getAllEmployees();
        console.log('✅ AttendanceFilters: Employees loaded:', data?.length, data);
        setEmployees(data || []);
      } catch (error) {
        console.error('❌ AttendanceFilters: Error loading employees:', error);
      }
    };
    loadEmployees();
  }, []);

  // Memoized filter change handler
  const handleFilterChange = useCallback(() => {
    if (!onFilterChange) return;

    const filters = {
      employeeId: selectedEmployee || null,
      filterType: dateFilterMode
    };

    // Add date range filters
    if (dateFilterMode === "dateRange" && startDate && endDate) {
      filters.startDate = startDate;
      filters.endDate = endDate;
    }

    // Add monthly filters - FIXED: Correct last day calculation
    if (dateFilterMode === "monthly" && selectedMonth && selectedYear) {
      const year = parseInt(selectedYear);
      const month = parseInt(selectedMonth);
      
      // Start date: First day of selected month
      filters.startDate = `${year}-${selectedMonth?.padStart(2, '0')}-01`;
      
      // End date: Last day of selected month
      // For month string "12" (December), parseInt gives 12
      // new Date(2025, 12, 0) gives last day of month index 11 (December) = Dec 31
      const lastDay = new Date(year, month, 0)?.getDate();
      filters.endDate = `${year}-${selectedMonth?.padStart(2, '0')}-${String(lastDay)?.padStart(2, '0')}`;
      
      console.log('📅 Monthly Filter Debug:', {
        selectedMonth,
        selectedYear,
        month,
        lastDay,
        startDate: filters?.startDate,
        endDate: filters?.endDate
      });
    }

    onFilterChange(filters);
  }, [selectedEmployee, dateFilterMode, startDate, endDate, selectedMonth, selectedYear, onFilterChange]);

  // Handle date filter mode change
  const handleDateFilterModeChange = (e) => {
    const value = e?.target?.value || e;
    setDateFilterMode(value);
    
    if (value !== "dateRange") {
      setStartDate("");
      setEndDate("");
    }
    if (value !== "monthly") {
      setSelectedMonth("");
    }
    
    // FIXED: Immediately trigger filter change when mode changes
    // This ensures filters are applied even when switching modes
    setTimeout(() => {
      if (onFilterChange) {
        const filters = {
          employeeId: selectedEmployee || null,
          filterType: value
        };
        
        // Preserve existing date range if switching back
        if (value === "dateRange" && startDate && endDate) {
          filters.startDate = startDate;
          filters.endDate = endDate;
        }
        
        // Preserve existing monthly filter if switching back
        if (value === "monthly" && selectedMonth && selectedYear) {
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth);
          filters.startDate = `${year}-${selectedMonth?.padStart(2, '0')}-01`;
          const lastDay = new Date(year, month, 0)?.getDate();
          filters.endDate = `${year}-${selectedMonth?.padStart(2, '0')}-${lastDay}`;
        }
        
        onFilterChange(filters);
      }
    }, 0);
  };

  // Handle employee selection
  const handleEmployeeChange = (e) => {
    const value = e?.target?.value || e;
    setSelectedEmployee(value);
    
    // FIXED: Automatically apply filter when employee is selected
    // This ensures the employee filter works immediately with date filters
    setTimeout(() => {
      if (onFilterChange) {
        const filters = {
          employeeId: value || null,
          filterType: dateFilterMode
        };
        
        // Include active date filters
        if (dateFilterMode === "dateRange" && startDate && endDate) {
          filters.startDate = startDate;
          filters.endDate = endDate;
        }
        
        if (dateFilterMode === "monthly" && selectedMonth && selectedYear) {
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth);
          filters.startDate = `${year}-${selectedMonth?.padStart(2, '0')}-01`;
          const lastDay = new Date(year, month, 0)?.getDate();
          filters.endDate = `${year}-${selectedMonth?.padStart(2, '0')}-${lastDay}`;
        }
        
        onFilterChange(filters);
      }
    }, 0);
  };

  // Handle month selection
  const handleMonthChange = (e) => {
    const value = e?.target?.value || e;
    setSelectedMonth(value);
  };

  // Handle year selection
  const handleYearChange = (e) => {
    const value = e?.target?.value || e;
    setSelectedYear(value);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setLoading(true);
    handleFilterChange();
    setTimeout(() => setLoading(false), 500);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedEmployee("");
    setDateFilterMode("all");
    setStartDate("");
    setEndDate("");
    setSelectedMonth("");
    setSelectedYear(currentYear?.toString());
    
    if (onFilterChange) {
      onFilterChange({
        employeeId: null,
        filterType: "all"
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center gap-2 mb-6">
        <Filter className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-800">Attendance Filters</h3>
      </div>

      {/* Employee Selection Section */}
      <div className="mb-6 pb-6 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Users className="w-4 h-4 inline mr-2" />
          Filter by Employee
        </label>
        <select
          value={selectedEmployee}
          onChange={handleEmployeeChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
        >
          <option value="">All Employees</option>
          {employees?.map((emp) => (
            <option key={emp?.id} value={emp?.id}>
              {emp?.userProfiles?.fullName || emp?.name || 'Unknown Employee'} {emp?.employeeId ? `(${emp?.employeeId})` : ''}
            </option>
          ))}
        </select>
        {selectedEmployee && (
          <p className="mt-2 text-xs text-gray-500">
            Showing attendance for selected employee only
          </p>
        )}
      </div>

      {/* Date Filter Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Clock className="w-4 h-4 inline mr-2" />
          Date Filter Mode
        </label>
        <select
          value={dateFilterMode}
          onChange={handleDateFilterModeChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white mb-4"
        >
          <option value="all">All Records (No Date Filter)</option>
          <option value="dateRange">Specific Date Range</option>
          <option value="monthly">Monthly Attendance</option>
        </select>

        {/* Specific Date Range Inputs */}
        {dateFilterMode === "dateRange" && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-3">Select Date Range</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e?.target?.value)}
                  min={startDate}
                  className="w-full"
                />
              </div>
            </div>
            {startDate && endDate && (
              <p className="mt-3 text-xs text-blue-600">
                Filtering from {formatEasternDateOnly(startDate)} to {formatEasternDateOnly(endDate)}
              </p>
            )}
          </div>
        )}

        {/* Monthly Filter Inputs */}
        {dateFilterMode === "monthly" && (
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm font-medium text-green-800 mb-3">Select Month & Year</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors bg-white"
                >
                  <option value="">Select Month</option>
                  {monthOptions?.map((month) => (
                    <option key={month?.value} value={month?.value}>
                      {month?.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors bg-white"
                >
                  {yearOptions?.map((year) => (
                    <option key={year?.value} value={year?.value}>
                      {year?.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {selectedMonth && selectedYear && (
              <p className="mt-3 text-xs text-green-600">
                Filtering monthly attendance for {monthOptions?.find(m => m?.value === selectedMonth)?.label} {selectedYear}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <Button
          onClick={handleApplyFilters}
          disabled={loading || (dateFilterMode === "dateRange" && (!startDate || !endDate)) || (dateFilterMode === "monthly" && !selectedMonth)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? "Applying..." : "Apply Filters"}
        </Button>
        <Button
          onClick={handleResetFilters}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg transition-colors"
        >
          Reset All
        </Button>
      </div>

      {/* Active Filter Summary */}
      {(selectedEmployee || dateFilterMode !== "all") && (
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <p className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Active Filters
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedEmployee && (
              <span className="px-3 py-1.5 bg-white text-blue-700 rounded-full text-xs font-medium shadow-sm border border-blue-200">
                👤 {employees?.find(e => e?.id === selectedEmployee)?.userProfiles?.fullName || employees?.find(e => e?.id === selectedEmployee)?.name || 'Selected Employee'}
              </span>
            )}
            {dateFilterMode === "dateRange" && startDate && endDate && (
              <span className="px-3 py-1.5 bg-white text-blue-700 rounded-full text-xs font-medium shadow-sm border border-blue-200">
                📅 {formatEasternDateOnly(startDate)} - {formatEasternDateOnly(endDate)}
              </span>
            )}
            {dateFilterMode === "monthly" && selectedMonth && selectedYear && (
              <span className="px-3 py-1.5 bg-white text-green-700 rounded-full text-xs font-medium shadow-sm border border-green-200">
                📆 {monthOptions?.find(m => m?.value === selectedMonth)?.label} {selectedYear}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceFilters;